import { useFormik } from 'formik';
import Image from 'next/image';
import { FC, useEffect, useMemo, useState } from 'react';
import Button from '~/components/Button';
import DropFiles from '~/components/DropFiles';
import FileItem from '~/components/Items/FileItem';
import Star from '~/components/Star';
import Text from '~/components/Text';
import TextArea from '~/components/TextArea';
import Colors from '~/constants/colors';
import { OrderItem } from '~/services/order';
import ModalContainer from '../ModalContainer';
import styles from './ReviewModal.module.scss';
import { toast } from 'react-toastify';
import UploadMediaItem from '~/components/UploadMediaItem';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { QUERY_GET_PRODUCT_ORDER } from '~/data/user';
import { isValidEmail } from '~/utils/validators';
import { UserAtom } from '~/services/user';
import { useRecoilValue } from 'recoil';
import { isEmpty, uniqBy } from 'lodash';
import { convertFilesToImages } from '~/utils/converters';
import { blobToBase64, scrollToFirstError } from '~/utils/helper';
import { ADD_PRODUCT_REVIEW, GET_REVIEW_META_DATA, QUERY_GET_REVIEW_PRODUCT_DETAIL } from '~/data/product/gql';
import LoadingContent from '~/components/LoadingContent';

interface ReviewModalProps {
    open?: boolean;
    onClose?: () => void;
    items: any[];
    order_id: string;
    defaultStart?: any;
}

interface IDefaultValues {
    [key: string]: {
        star: number;
        review: string;
        files: any[];
        name: any;
        content: string;
        summary: any;
    };
}

const ReviewModal: FC<ReviewModalProps> = ({ open, onClose, items = [], defaultStart }) => {
    const [defaultValues, setDefaultValues] = useState<IDefaultValues>({});
    const currentUser = useRecoilValue(UserAtom.currentUser);
    const [reviewData, setReviewData] = useState<any>({});
    const [loading, setLoading] = useState(false);
    // query

    // const [getReviewBySlug] = useLazyQuery(QUERY_GET_REVIEW_PRODUCT_DETAIL, {
    //     notifyOnNetworkStatusChange: true
    // });
    const [addProductReview, { error }] = useMutation(ADD_PRODUCT_REVIEW);
    const [queryActual, { data: actualData }] = useLazyQuery<any>(QUERY_GET_PRODUCT_ORDER, {
        context: { fetchOptions: { method: 'GET' } }
    });
    const { data: reviewMeta } = useQuery(GET_REVIEW_META_DATA);

    // get review meta data
    const reviewMetaDataValue = reviewMeta?.productReviewRatingsMetadata?.items?.[0]?.values;
    const ratingsId = reviewMeta?.productReviewRatingsMetadata?.items?.[0]?.id;
    // formik
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: defaultValues,
        validate: (values) => {
            const errors: Record<string, any> = {};

            const keys = Object.keys(values);
            keys.forEach((key) => {
                const value = values[key];

                const errors: Record<string, string> = {};
                // if (!values.name?.trim()) {
                //   errors.name = "Please enter your name";
                // }
                if (!value.content?.trim()) {
                    errors.content = 'Please enter your review content';
                }
                if (!value.summary?.trim()) {
                    errors.summary = 'Please enter your review title';
                } else if (!isValidEmail(value.summary)) {
                    errors.summary = 'Email is not valid';
                }
                if (!value?.star) {
                    errors.star = 'Please enter your rating';
                }
            });

            return errors;
        },
        onSubmit: (values) => {
            const newPromise: any = [];
            items?.forEach((item) => {
                newPromise.push(submitReviewById(item.product_sku, values[item.product_sku]));
            });
            setLoading(true);
            Promise.all(newPromise)
                .then(() => {
                    onClose?.();
                    toast.success('Submit review successfully!');
                    formik.resetForm();
                    setLoading(false);
                })
                .catch((err) => {
                    console.error(err);
                    toast.error('Submit review failed!');
                    setLoading(false);
                });
        }
    });
    //function
    const checkForUpload = async (file: IFile) => {
        let isAllow = false;
        let compressedFile = file;
        let compressedFileBase64 = null;
        if (file) {
            const options = {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true
            };
            try {
                isAllow = file.size < 2 * 1024 * 1024;
                compressedFileBase64 = await blobToBase64(file.file);
            } catch (error) {
                console.error(error);
            }
        }

        return {
            isAllow,
            compressedFile,
            url: compressedFileBase64
        };
    };

    const handleChangeUpload = async (files: IFile[], sku: any) => {
        const currentFiles = formik.values?.[sku]?.files || [];
        const imageFiles = files?.filter(
            (file) => currentFiles?.findIndex((currentFile) => currentFile.name === file.name) === -1
        );
        if (isEmpty(imageFiles)) {
            return;
        }
        const remainingFilesAllowUpload = 3 - currentFiles?.length;
        const newFiles = remainingFilesAllowUpload === 0 ? [] : imageFiles.slice(0, remainingFilesAllowUpload);
        const totalLengthFile = currentFiles.length + newFiles.length;

        if (totalLengthFile > 3) {
            // toast.e({ type: 'error', message: 'You can upload maximum 3 image' })
            formik.setFieldError(sku + '.files', 'You can upload maximum 3 image');
            // setLoadingUpload(false)

            return;
        }
        const getLastThreeFile = [];
        // const tempData =  imageFiles;
        for (let i = 0; i < newFiles.length; i++) {
            getLastThreeFile.push(newFiles[i]);
        }
        const arrPromise: any = [];
        getLastThreeFile.map((file) => {
            arrPromise.push(
                new Promise((res, rej) => {
                    checkForUpload(file)
                        .then((response) => {
                            if (!response.isAllow) {
                                rej("Image must smaller than 2MB!'");
                            }
                            res({
                                ...file,
                                url: response.url
                            });
                        })
                        .catch((err) => rej(err));
                })
            );
        });

        Promise.all(arrPromise)
            .then((values) => {
                formik.setFieldValue(sku + '.files', currentFiles.concat(values));
            })
            .catch((err) => {
                formik.setFieldError(sku + '.files', err);
            });
    };

    const submitReviewById = async (sku: string, data: any) => {
        const { name, content, summary, files, star } = data;

        const ratingValue = reviewMetaDataValue
            ?.slice(0)
            .find((item: any, index: number) => index + 1 === star)?.value_id;
        const images = files?.map((file: any) => file.url) || [];
        return await addProductReview({
            variables: {
                sku,
                nickname: name?.trim() || summary,
                summary: summary,
                text: content,
                ratingsId: ratingsId,
                ratingsValueId: ratingValue,
                images
            }
        });
    };

    // get actual product data
    useEffect(() => {
        if (!!items?.length && !!open) {
            // items?.forEach((item) => {
            //     getReviewBySlug({
            //         variables: {
            //             param: item.product_url_key
            //         },
            //         onCompleted(data) {
            //             setReviewData((prev: any) => {
            //                 return {
            //                     ...prev,
            //                     [item.product_sku]: data
            //                 };
            //             });
            //         }
            //     });
            // });
            queryActual({
                variables: {
                    url_key: items?.map((item) => item.product_url_key)
                }
            });
        }
    }, [items, open]);

    // init default values
    useEffect(() => {
        const values: IDefaultValues = {};
        items?.forEach((item) => {
            if (!!item.product_sku) {
                values[item.product_sku] = {
                    star: defaultStart || 5,
                    review: '',
                    files: [] as IFile[],
                    name: currentUser?.firstname + ' ' + currentUser?.lastname,
                    content: '',
                    summary: currentUser?.email
                };
            }
        });

        setDefaultValues(values);
    }, [items]);

    const products = useMemo(() => {
        return items?.map((item) => {
            const actualProduct = actualData?.products?.items?.find((actualItem: any) => {
                return actualItem?.url_key === item?.product_url_key;
            });
            const selectVariant = actualProduct?.variants?.find(
                (variant: any) => variant?.product?.sku === item?.product_sku
            );
            return {
                ...(actualProduct || {}),
                ...item,
                selectVariant
            };
        });
    }, [actualData, items]);

    useEffect(() => {
        if (!!defaultStart) {
            products?.map((item) => {
                formik.setFieldValue(`${item.product_sku}.star`, defaultStart);
            });
        }
    }, [defaultStart]);
    return (
        <ModalContainer
            open={open}
            onClose={() => {
                formik.resetForm();
                onClose?.();
            }}
            width={856}
            className={styles.review_modal}
            title="Review product"
            titleIcon="star"
            type="bottom-sheet"
        >
            {products?.map((item) => {
                const files = formik.values?.[item.product_sku]?.files || [];
                const star = formik.values?.[item.product_sku]?.star || 0;
                return (
                    <div className={styles.content} key={item.product_sku}>
                        <div className="flex gap-3">
                            <img
                                src={item.thumbnail?.url || ''}
                                alt={item.thumbnail?.url || ''}
                                width={68}
                                height={68}
                                className="object-contain h-fit flex-shrink-0"
                            />
                            <div className="flex flex-col gap-1 flex-1">
                                <Text className="line-clamp-2" type="title-5" color={Colors.GREY_900}>
                                    {item.product_name}
                                </Text>
                                <div className="flex items-center gap-3">
                                    <Text type="body-3" color={Colors.GREY_700}>
                                        {item.selectVariant?.attributes?.map((attr: any) => attr.label).join(' / ')}
                                    </Text>

                                    <Text type="body-3" color={Colors.GREY_800}>
                                        x{item.quantity_ordered}
                                    </Text>
                                </div>
                                <div className="flex gap-y-3 gap-x-4 justify-between md:items-center flex-col md:flex-row">
                                    <Text className="mt-1" type="title-5" color={Colors.PRIMARY}>
                                        $ {item?.product_sale_price?.value?.toFixed(2)}
                                    </Text>
                                    <Star
                                        isEdit
                                        value={star}
                                        onChange={(v) => formik.setFieldValue(`${item.product_sku}.star`, v)}
                                    />
                                </div>
                            </div>
                        </div>
                        <TextArea
                            label="Your review"
                            placeholder="Enter your review"
                            rows={5}
                            {...formik.getFieldProps(`${item.product_sku}.content`)}
                            error={
                                formik.touched?.[item.product_sku]?.content &&
                                formik.errors?.[item.product_sku]?.content
                            }
                        />
                        <div className="flex flex-col">
                            <Text type="title-5" color={Colors.GREY_800}>
                                Add images review
                            </Text>
                            <Text type="body-4" color={Colors.GREY_700} className="mt-2 mb-3">
                                You can upload maximum 3 images.
                            </Text>
                            <div className="flex flex-col gap-3">
                                {!!files.length ? (
                                    <div className="flex flex-wrap gap-x-3 gap-y-2">
                                        {files.map((file) => (
                                            <FileItem
                                                file={file}
                                                key={file.url}
                                                onDelete={() => {
                                                    formik.setFieldValue(
                                                        `${item.product_sku}.files`,
                                                        files.filter((f) => f.url !== file.url)
                                                    );
                                                    URL.revokeObjectURL(file.url);
                                                }}
                                            />
                                        ))}
                                        {files.length < 3 && (
                                            <UploadMediaItem
                                                isMultiple
                                                containerSetting={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: 4,
                                                    border: '1px dashed',
                                                    cursor: 'pointer'
                                                }}
                                                onFileChange={(filesResult) => {
                                                    const newFiles = (
                                                        formik.values?.[item.product_sku]?.files || []
                                                    ).concat(convertFilesToImages(filesResult));

                                                    handleChangeUpload(
                                                        uniqBy(newFiles, 'url') as any,
                                                        item.product_sku
                                                    );
                                                }}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <DropFiles
                                        accept="image/png,image/jpg,image/jpeg"
                                        onDrop={(newFiles) => handleChangeUpload(newFiles, item.product_sku)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
            <div className="sticky bottom-0 py-4 bg-white">
                <Button
                    className="md:max-w-[200px] mx-auto w-full min-h-10"
                    onClick={formik.submitForm}
                    loading={loading}
                >
                    Submit Review
                </Button>
            </div>
        </ModalContainer>
    );
};

export default ReviewModal;
